// Toast service that can be used outside of React components
type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastMessage {
  type: ToastType;
  title: string;
  message?: string;
}

let toastCallback: ((toast: ToastMessage) => void) | null = null;

export const toastService = {
  // Initialize the toast service with the callback from the context
  initialize: (callback: (toast: ToastMessage) => void) => {
    toastCallback = callback;
  },

  // Toast methods
  success: (title: string, message?: string) => {
    if (toastCallback) {
      toastCallback({ type: 'success', title, message });
    }
  },

  error: (title: string, message?: string) => {
    if (toastCallback) {
      toastCallback({ type: 'error', title, message });
    }
  },

  warning: (title: string, message?: string) => {
    if (toastCallback) {
      toastCallback({ type: 'warning', title, message });
    }
  },

  info: (title: string, message?: string) => {
    if (toastCallback) {
      toastCallback({ type: 'info', title, message });
    }
  }
};

// Helper function to create user-friendly error messages from API errors
export const getErrorMessage = (error: any): { title: string; message?: string } => {
  if (error?.response?.status === 429) {
    return {
      title: 'Too Many Requests',
      message: 'Please wait a moment and try again.'
    };
  }
  if (error?.response?.status === 500) {
    return {
      title: 'Server Error',
      message: 'Please try again later.'
    };
  }
  if (error?.response?.status === 404) {
    return {
      title: 'Data Not Found',
      message: 'Please check your filters or try again.'
    };
  }
  if (error?.response?.status === 403) {
    return {
      title: 'Access Denied',
      message: 'You don\'t have permission to access this data.'
    };
  }
  if (error?.response?.status === 401) {
    return {
      title: 'Authentication Required',
      message: 'Please log in again.'
    };
  }
  if (error?.message) {
    return {
      title: 'Error',
      message: error.message
    };
  }
  return {
    title: 'Something went wrong',
    message: 'An unexpected error occurred. Please try again.'
  };
};